import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useTenantSlug } from './TenantSlugContext';
import { normalizeTenantSlug } from '../utils/tenantSlug';
import {
  TenantSiteProfile,
  TenantSiteProfileService,
} from '../services/TenantSiteProfileService';

interface ClinicProfileContextValue {
  profile: TenantSiteProfile | null;
  isLoading: boolean;
  isTenantContext: boolean;
  effectiveTenantSlug: string | null;
  refreshProfile: () => Promise<void>;
}

const defaultValue: ClinicProfileContextValue = {
  profile: null,
  isLoading: false,
  isTenantContext: false,
  effectiveTenantSlug: null,
  refreshProfile: async () => undefined,
};

const ClinicProfileContext = createContext<ClinicProfileContextValue>(defaultValue);

interface ClinicProfileProviderProps {
  children: ReactNode;
}

export const ClinicProfileProvider = ({ children }: ClinicProfileProviderProps) => {
  const { selectedClient } = useAuth();
  const { tenantSlug, resolvedTenant } = useTenantSlug();
  const [profile, setProfile] = useState<TenantSiteProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const profileRequestIdRef = useRef(0);

  const isTenantContext = Boolean(selectedClient || resolvedTenant);
  const effectiveTenantSlug = useMemo(() => {
    const slugCandidate =
      selectedClient?.clientSlug ?? resolvedTenant?.clientSlug ?? tenantSlug ?? '';
    const normalized = normalizeTenantSlug(slugCandidate);
    return normalized || null;
  }, [resolvedTenant?.clientSlug, selectedClient?.clientSlug, tenantSlug]);

  const profileContextKey = useMemo(
    () => selectedClient?.clientId
      ? `authenticated:${selectedClient.clientId}`
      : effectiveTenantSlug
        ? `public:${resolvedTenant?.clientId ?? ''}:${effectiveTenantSlug}`
        : 'empty',
    [effectiveTenantSlug, resolvedTenant?.clientId, selectedClient?.clientId],
  );
  const profileContextKeyRef = useRef(profileContextKey);
  profileContextKeyRef.current = profileContextKey;

  const refreshProfile = useCallback(async () => {
    const requestedContextKey = profileContextKey;
    if (profileContextKeyRef.current !== requestedContextKey) {
      return;
    }

    const requestId = ++profileRequestIdRef.current;

    if (!selectedClient?.clientId && !effectiveTenantSlug) {
      setProfile(null);
      setIsLoading(false);
      return;
    }

    // Nunca mantenha o perfil do tenant anterior visível durante a troca. As
    // telas temporais tratam profile=null como estado bloqueado/fail-closed.
    setProfile(null);
    setIsLoading(true);

    try {
      const nextProfile = selectedClient?.clientId
        ? await TenantSiteProfileService.resolveForAuthenticatedClient(selectedClient)
        : await TenantSiteProfileService.resolveForPublicSlug(effectiveTenantSlug as string);

      if (
        requestId === profileRequestIdRef.current
        && profileContextKeyRef.current === requestedContextKey
      ) {
        setProfile(nextProfile);
      }
    } catch (error) {
      console.error(
        selectedClient?.clientId
          ? 'Erro ao resolver perfil da clínica autenticada:'
          : 'Erro ao resolver perfil público da clínica:',
        error,
      );

      if (
        requestId === profileRequestIdRef.current
        && profileContextKeyRef.current === requestedContextKey
      ) {
        setProfile(null);
      }
    } finally {
      if (
        requestId === profileRequestIdRef.current
        && profileContextKeyRef.current === requestedContextKey
      ) {
        setIsLoading(false);
      }
    }
  }, [effectiveTenantSlug, profileContextKey, selectedClient]);

  useEffect(() => {
    void refreshProfile();

    return () => {
      profileRequestIdRef.current += 1;
    };
  }, [refreshProfile, resolvedTenant?.clientId]);

  const value = useMemo<ClinicProfileContextValue>(
    () => ({
      profile,
      isLoading,
      isTenantContext,
      effectiveTenantSlug,
      refreshProfile,
    }),
    [effectiveTenantSlug, isLoading, isTenantContext, profile, refreshProfile],
  );

  return <ClinicProfileContext.Provider value={value}>{children}</ClinicProfileContext.Provider>;
};

export const useClinicProfile = () => useContext(ClinicProfileContext);
